import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type UseInfiniteQueryResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { useApi } from '@/lib/api-context';
import { useActiveCompany } from '@/lib/active-company-context';
import { rolesResponseSchema, type Role } from '@/features/roles-access/types';

import { adminUsersResponseSchema, type AdminUsersPage } from './types';

const USERS_KEY = 'admin-users';
const PAGE_SIZE = 50;

/** Roles reales (id, code, name) para el selector de asignación y el mapeo
 *  code→id al quitar. Global (no depende de empresa). */
export function useRoles({ enabled }: { enabled: boolean }): UseQueryResult<Role[]> {
  const api = useApi();
  return useQuery({
    queryKey: ['admin-roles'],
    queryFn: async () => {
      const raw = await api.get<unknown>('/api/v1/admin/roles');
      return rolesResponseSchema.parse(raw).data;
    },
    enabled,
  });
}

export interface UsersQuery {
  q?: string;
  status?: string;
}

/** Filtros de la vista como parámetros del endpoint (sin paginación). */
function filterParams(query: UsersQuery): URLSearchParams {
  const qs = new URLSearchParams();
  if (query.q) {
    qs.set('q', query.q);
  }
  if (query.status) {
    qs.set('status', query.status);
  }
  return qs;
}

/** Filtros para exportar (spec 2026-10-14 §3): los de la vista, sin cursor ni tamaño. */
export function usersExportSearch(query: UsersQuery): string {
  return filterParams(query).toString();
}

function toSearch(query: UsersQuery, cursor: string | null): string {
  const qs = new URLSearchParams({ limit: String(PAGE_SIZE) });
  for (const [k, v] of filterParams(query)) {
    qs.set(k, v);
  }
  if (cursor) {
    qs.set('cursor', cursor);
  }
  return qs.toString();
}

/** Directorio de usuarios de la pantalla de Usuarios: búsqueda, estado y
 *  paginación por cursor (append). Cada página se valida con Zod. */
export function useUsersDirectory(
  query: UsersQuery,
  { enabled }: { enabled: boolean },
): UseInfiniteQueryResult<InfiniteData<AdminUsersPage>> {
  const api = useApi();
  const { activeCompanyId } = useActiveCompany();
  return useInfiniteQuery({
    queryKey: [USERS_KEY, 'directory', activeCompanyId, query],
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) => {
      const raw = await api.get<unknown>(`/api/v1/admin/users?${toSearch(query, pageParam)}`);
      return adminUsersResponseSchema.parse(raw);
    },
    getNextPageParam: (last) => last.meta.next_cursor,
    enabled: enabled && Boolean(activeCompanyId),
  });
}

/** Asigna un rol a un usuario. Invalida los listados de usuarios al terminar. */
export function useAssignRole(): ReturnType<
  typeof useMutation<void, Error, { userId: string; roleId: string }>
> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, roleId }) => {
      await api.post(`/api/v1/admin/users/${userId}/roles`, { roleId });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [USERS_KEY] }),
  });
}

/** Quita un rol de un usuario. Invalida los listados de usuarios al terminar. */
export function useRemoveRole(): ReturnType<
  typeof useMutation<void, Error, { userId: string; roleId: string }>
> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, roleId }) => {
      await api.delete(`/api/v1/admin/users/${userId}/roles/${roleId}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [USERS_KEY] }),
  });
}

export type UserStatusAction = 'deactivate' | 'reactivate';

/** Desactiva o reactiva un usuario. Invalida los listados de usuarios al terminar. */
export function useSetUserStatus(): ReturnType<
  typeof useMutation<void, Error, { userId: string; action: UserStatusAction }>
> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, action }) => {
      await api.post(`/api/v1/admin/users/${userId}/${action}`, {});
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [USERS_KEY] }),
  });
}
